import { QueryFunctionContext } from "@tanstack/react-query";
import getAxiosInstance from "../axiosClient";
import type {
  iMetaData,
  iSummaryPayload,
  iSummaryAPIPayload,
  iSummaryResponse,
  iAnalyticsPayload
} from "../../interfaces/analytics_v1";
import { calculatePercentageChange } from "./utils";
import { EEA, REGULATION_MAP } from "../../utils/constants";
// @flow
const requestAnalytics = getAxiosInstance(process.env.REACT_APP_CHOICE_2_V1_ANALYTICS_API_URL);

async function getSummaryForDateRange(pCode: string, payload: iSummaryAPIPayload): Promise<iSummaryResponse> {
  const data = await requestAnalytics.post(`/summary/pcode/${pCode}`, { ...payload });
  return data;
}

async function getAnalyticsDataAPI(pCode: string, payload: iAnalyticsPayload): Promise<any> {
  const data = await requestAnalytics.post(`/graph/pcode/${pCode}`, { ...payload });
  return data;
}

export async function getAnalyticsMetaData(context: QueryFunctionContext): Promise<iMetaData> {
  const data = await requestAnalytics.get("/metadata");
  return data;
}

export async function getSummaryData(context: QueryFunctionContext): Promise<iAnalyticsDataList> {
  const [, queryPayload] = context.queryKey;
  const { summaryDateRange, comparisonDateRange, metricFields, propertyId, pCode }: iSummaryPayload = queryPayload;

  const getPayload = (dateRange, metricFields) => ({
    filters: [
      {
        key: "startDate",
        operator: "gteq",
        values: [dateRange[0]]
      },
      {
        key: "endDate",
        operator: "lteq",
        values: [dateRange[1]]
      }
      // {
      //   key: "propertyId",
      //   operator: "in",
      //   values: propertyId
      // }
    ],
    metrics: metricFields
  });

  const [summaryData, comparisionData] = await Promise.all([
    getSummaryForDateRange(pCode, getPayload(summaryDateRange, metricFields)),
    getSummaryForDateRange(pCode, getPayload(comparisonDateRange, metricFields))
  ]);

  const data = Object.keys(summaryData).map(key => ({
    fieldName: key,
    value: summaryData[key],
    comparisonPercentage: calculatePercentageChange(comparisionData[key], summaryData[key])
  }));

  return data;
}

export async function getAnalyticsData(context: QueryFunctionContext): Promise<iAnalyticsDataList> {
  const [, queryPayload] = context.queryKey;
  const { pCode, filters, queries }: iAnalyticsPayload = queryPayload;
  const metricsData = await getAnalyticsDataAPI(pCode, { filters, queries });

  const processedData = [];
  const processMetric = {
    region_wise_traffic(metric) {
      const resultsObj = metric.results.reduce((acc, curr) => {
        const { period, country, totalTraffic } = curr;
        if (!acc[period]) {
          acc[period] = { totalTraffic: 0, europeTraffic: 0, usTraffic: 0 };
        }
        acc[period].totalTraffic += totalTraffic;
        if (EEA.includes(country)) acc[period].europeTraffic += totalTraffic;
        if (country === "US") acc[period].usTraffic += totalTraffic;
        return acc;
      }, {});

      const updatedResults = Object.keys(resultsObj).flatMap(period => [
        { period, country: "US", totalTraffic: resultsObj[period].usTraffic },
        { period, country: "EU", totalTraffic: resultsObj[period].europeTraffic },
        { period, country: "Total", totalTraffic: resultsObj[period].totalTraffic }
      ]);

      processedData.push({ id: metric.id, results: updatedResults });
    },

    page_wise_total_sessions(metric) {
      const totalsObj = metric.results.reduce((acc, curr) => {
        acc[curr.period] = (acc[curr.period] || 0) + curr.totalSessions;
        return acc;
      }, {});

      const updatedResults = [
        ...metric.results,
        ...Object.keys(totalsObj).map(period => ({
          period,
          pageFormat: "Total",
          totalSessions: totalsObj[period]
        }))
      ];

      processedData.push({ id: metric.id, results: updatedResults });
    },

    regulation_wise_consent_distribution(metric) {
      ["1", "2"].forEach(regulation => {
        const result = metric.results.find(item => item.regulation === regulation);
        if (result) {
          processedData.push({
            id: `${REGULATION_MAP[regulation].toLowerCase()}_consent_distribution`,
            results: [result]
          });
        }
      });
    }
  };

  metricsData.forEach(metric => {
    if (processMetric[metric.id]) {
      processMetric[metric.id](metric);
    } else {
      processedData.push(metric);
    }
  });

  console.log("processedData", processedData);
  return processedData;
}

export async function getTrafficConsentTableData(context: QueryFunctionContext): Promise<iAnalyticsDataList> {
  const [, queryPayload] = context.queryKey;
  const { currentDate, comparisonDate }: iTableDataPayload = queryPayload;
  const { pCode }: iAnalyticsPayload = currentDate;

  const [currentDateAnalytics, comparisonDateAnalytics] = await Promise.all([
    getAnalyticsDataAPI(pCode, { filters: currentDate.filters, queries: currentDate.queries }),
    getAnalyticsDataAPI(pCode, { filters: comparisonDate.filters, queries: comparisonDate.queries })
  ]);

  if (currentDateAnalytics.length === 0) {
    return currentDateAnalytics;
  }
  const processedData = currentDateAnalytics.map(metric => {
    const comparisonItem = comparisonDateAnalytics.find(({ id }) => id === metric.id);
    if (comparisonItem) {
      const updatedResults = metric.results.map(item => {
        const comparisonResult = comparisonItem.results.find(({ country }) => country === item.country);
        return {
          ...item,
          totalTrafficChange: comparisonResult
            ? calculatePercentageChange(comparisonResult.totalTraffic, item.totalTraffic)
            : 0,
          optInPercentChange: comparisonResult
            ? calculatePercentageChange(comparisonResult.optInPercent, item.optInPercent)
            : 0
        };
      });

      return { id: metric.id, results: updatedResults };
    }
    return metric;
  });

  console.log("processedData  ", processedData);

  return processedData;
}
